import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import CodeOrderTree from '../components/CodeOrderTree'
import SupplyBuilder from '../components/SupplyBuilder'
import { formatDate, todayISO } from '../lib/format'
import { useEscClose } from '../lib/useEscClose'
import { codeOrdersApi, type PortalMe } from './api'
import type { CodeOrderDetail } from '../types'

/**
 * Заказы кодов в кабинете клиента.
 *
 * То же дерево, что видим мы. Ради этого всё и делалось: чтобы не пересылать
 * таблицы почтой и не выяснять потом, по какой из версий заказывали коды.
 *
 * Разбор подтверждает клиент: это его поставка и его номера паллет. Мы можем
 * подтвердить за него, но правильнее, когда числа глазами проверяет тот, кто
 * их написал.
 */
export default function PortalCodeOrders({ me }: { me: PortalMe }) {
  const [openId, setOpenId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const list = useQuery({
    queryKey: ['portal', 'code-orders'],
    queryFn: () => codeOrdersApi.list(),
  })

  const rows = list.data ?? []

  return (
    <>
      <h1 className="page-title">Заказы кодов</h1>
      <p className="hint">
        Поставки и коды маркировки по ним. Присылайте файл заказа — мы разберём
        его на грузовики, товары и паллеты; а если таблицы нет, поставку можно
        собрать прямо здесь. Дальше работаем по дереву, которое видно обеим
        сторонам.
      </p>

      <div className="toolbar">
        <button className="btn primary" onClick={() => setCreating(true)}>
          + Новая поставка
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>Всего: {rows.length}</div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="col-num">№</th>
              <th>Дата</th>
              <th>Поставка</th>
              <th>Этап</th>
              <th className="num">Грузовиков</th>
              <th className="num">Паллет</th>
              <th className="num">Единиц</th>
              <th>Обновлено</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={8} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  Поставок пока нет
                </td>
              </tr>
            )}
            {rows.map((o) => (
              <tr key={o.id}>
                <td className="task-num">{o.number || o.id}</td>
                <td className="nowrap">{formatDate(o.supply_date)}</td>
                <td>
                  <button className="link-btn" onClick={() => setOpenId(o.id)}>
                    {o.title}
                  </button>
                </td>
                <td className="nowrap">
                  <span className={`co-chip co-${o.stage}`}>{o.stage_title}</span>
                </td>
                <td className="num">{o.trucks_count || '—'}</td>
                <td className="num">{o.pallets_count || '—'}</td>
                <td className="num">{o.units_total ? o.units_total.toLocaleString('ru') : '—'}</td>
                <td className="nowrap">{formatDate(o.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewDialog onClose={() => setCreating(false)} onCreated={setOpenId} />}
      {openId !== null && (
        <OrderDialog id={openId} me={me} onClose={() => setOpenId(null)} />
      )}
    </>
  )
}

function OrderDialog({
  id,
  me,
  onClose,
}: {
  id: number
  me: PortalMe
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [собираем, setСобираем] = useState(false)

  useEscClose(onClose)

  const order = useQuery({
    queryKey: ['portal', 'code-order', id],
    queryFn: () => codeOrdersApi.get(id),
  })

  // Наш справочник товаров: клиент собирает поставку из тех же карточек, по
  // которым мы потом заказываем коды, — иначе названия разойдутся.
  const товары = useQuery({
    queryKey: ['portal', 'code-order', id, 'products'],
    queryFn: () => codeOrdersApi.products(id),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['portal', 'code-order', id] })
    qc.invalidateQueries({ queryKey: ['portal', 'code-orders'] })
  }

  async function действие(что: () => Promise<CodeOrderDetail>) {
    setBusy(true)
    setError('')
    try {
      await что()
      refresh()
    } catch (e: any) {
      setError(e?.message ?? 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const data = order.data

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal huge tkw">
        <div className="tkw-head">
          <div className="tkw-titles">
            <div className="tkw-num">
              {data?.number ? `Поставка № ${data.number}` : `Заказ кодов № ${id}`}
              {data?.supply_date && (
                <span className="tkw-date">от {formatDate(data.supply_date)}</span>
              )}
              {data && <span className={`co-chip co-${data.stage}`}>{data.stage_title}</span>}
            </div>
            {data && <div className="tkw-subject">{data.title}</div>}
          </div>
          <button className="tkw-close" title="Закрыть окно" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && (
          <div className="error" style={{ margin: '10px 20px 0' }}>
            {error}
          </div>
        )}
        {order.isLoading && <p className="empty">Загрузка…</p>}

        {data && (
          <div className="tkw-body">
            <div className="tkw-main">
              <CodeOrderTree
                order={data}
                onDownload={(fileId, name) =>
                  void codeOrdersApi
                    .download(id, fileId, name)
                    .catch((e: any) => setError(e?.message ?? 'Не удалось скачать'))
                }
                actions={
                  // Пока разбор не подтверждён, поставку можно заводить и
                  // пересобирать. После — только через менеджера.
                  data.stage === 'new' || data.stage === 'parsed'
                    ? {
                        busy,
                        onSource: (f) =>
                          void действие(() => codeOrdersApi.uploadSource(id, f)),
                        onBuild: () => setСобираем(true),
                      }
                    : undefined
                }
              />
            </div>

            <div className="tkw-aside">
              <div className="tkw-block">
                <div className="tkw-block-title">Что сейчас</div>
                <ol className="co-steps">
                  {data.steps.map((s) => (
                    <li
                      key={s.stage}
                      className={s.current ? 'current' : s.done ? 'done' : undefined}
                    >
                      {s.title}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="tkw-block">
                <div className="tkw-block-title">Состав поставки</div>
                {data.files.length === 0 && (
                  <p className="hint" style={{ marginTop: 0 }}>
                    Пришлите таблицу поставки — ту же, по которой работаете сами.
                    Или соберите её здесь, если таблицы нет.
                  </p>
                )}
                {data.files.map((f) => (
                  <button
                    key={f.id}
                    className="tk-file"
                    onClick={() =>
                      void codeOrdersApi
                        .download(id, f.id, f.original_name)
                        .catch((e: any) => setError(e?.message ?? 'Не удалось скачать'))
                    }
                  >
                    📊 {f.original_name}
                  </button>
                ))}
                <label className="btn small tkw-file-add" style={{ cursor: 'pointer' }}>
                  {busy ? 'Разбираем…' : '📥 Загрузить файл заказа'}
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    disabled={busy}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void действие(() => codeOrdersApi.uploadSource(id, f))
                    }}
                  />
                </label>
                {(data.stage === 'new' || data.stage === 'parsed') && (
                  <button
                    className="btn small"
                    disabled={busy}
                    onClick={() => setСобираем(true)}
                  >
                    ✍ {data.trucks.length ? 'Пересобрать без файла' : 'Собрать без файла'}
                  </button>
                )}
              </div>

              {data.notes.length > 0 && (
                <div className="tkw-block">
                  <div className="tkw-block-title">Проверьте эти строки</div>
                  <p className="hint" style={{ marginTop: 0 }}>
                    Мы ничего не исправляли сами — непонятно, какое из чисел верное.
                  </p>
                  {data.notes.map((n, i) => (
                    <div key={i} className="cot-note">
                      ⚠ {n}
                    </div>
                  ))}
                </div>
              )}

              {data.stage === 'parsed' && (
                <div className="tkw-block">
                  <div className="tkw-block-title">Всё ли разложено верно?</div>
                  <p className="hint" style={{ marginTop: 0 }}>
                    {me.full_name}, сверьте дерево {data.files.length ? 'со своим файлом' : 'со своими бумагами'}:
                    грузовики, товары, номера паллет. После подтверждения по нему заказываются
                    коды — переделывать будет поздно.
                  </p>
                  <button
                    className="btn primary"
                    disabled={busy}
                    onClick={() => void действие(() => codeOrdersApi.confirm(id))}
                  >
                    Да, разложено верно
                  </button>
                </div>
              )}

              {data.confirmed_at && (
                <div className="tkw-block">
                  <div className="tkw-block-title">Разбор подтверждён</div>
                  <p className="hint" style={{ marginTop: 0 }}>
                    {formatDate(data.confirmed_at)}. Нужно что-то поменять — напишите
                    вашему менеджеру, он вернёт поставку к проверке.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {собираем && data && (
        <SupplyBuilder
          order={data}
          products={товары.data ?? []}
          onPreview={(body) => codeOrdersApi.buildPreview(id, body)}
          onSave={async (body) => {
            const r = await codeOrdersApi.build(id, body)
            refresh()
            return r
          }}
          onClose={() => setСобираем(false)}
        />
      )}
    </div>
  )
}

function NewDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [дата, setДата] = useState(todayISO())
  const [error, setError] = useState('')

  useEscClose(onClose)

  const create = useMutation({
    mutationFn: () => codeOrdersApi.create({ title: title.trim(), supply_date: дата }),
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: ['portal', 'code-orders'] })
      onClose()
      onCreated(o.id)
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось создать'),
  })

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 480 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          create.mutate()
        }}
      >
        <h2>Новая поставка</h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <div className="field">
            <label htmlFor="pco-date">Дата поставки</label>
            <input
              id="pco-date"
              type="date"
              value={дата}
              onChange={(e) => setДата(e.target.value)}
              required
              autoFocus
            />
            <span className="hint-inline">Номер программа присвоит сама</span>
          </div>

          <div className="field full">
            <label htmlFor="pco-title">Название поставки</label>
            <input
              id="pco-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Поставка от ${formatDate(дата)}`}
            />
            <span className="hint-inline">
              Необязательно. Так, как вы называете её у себя, — чтобы говорить об
              одном и том же
            </span>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={create.isPending || !дата}>
            {create.isPending ? 'Создаём…' : 'Создать'}
          </button>
        </div>
      </form>
    </div>
  )
}
